/**
 * Abrechnung Stufe 1 — die Drawer des Reiters „Abrechnen“ (Mockup 28.09.2026, Zustände 3, 4, 5, 10):
 * Leistung, Rabatt/Zuschlag, Mehr-/Minderverbrauch, Zahlungen/Rechnungen außerhalb von MAESTRO und
 * die Vorschau der Schlussrechnung vor Tor 1. Die Vorschau-Zahlen in den Drawern rechnet dieselbe
 * Funktion wie der Server (`lib/abrechnung-anzeige.ts#vorschau`); gespeichert wird nur über die API.
 */
import { useEffect, useState, type ReactNode } from "react";
import { vatFromGross } from "@maestro/pricing";
import { Button, Checkbox, DateInput, Drawer, ErrorNote, Icon, Input, Radio, Segmente, Select, Textarea } from "../components";
import { money, parseGermanEurosToCents, parseGermanDecimal } from "../format";
import { bpAlsEingabe, einfuegeIndex, einfuegeIndexRabatt, satzText, vorschau, zeilenTitel } from "../../lib/abrechnung-anzeige";
import type { AbrAntwort, AbrZeile, AbrZeileEingabe, Bezug, VorschauDaten } from "./typen";

const eurFeld = (c: number | null | undefined) => (c == null ? "" : (c / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Segmente liegt seit sql/720 (Anzahlungs-Frist-Modus) in ../components — hier weiter exportiert.
export { Segmente };

function Feld({ label, children, hinweis }: { label: string; children: ReactNode; hinweis?: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block font-label-md text-[12px] font-semibold text-on-surface-variant dark:text-secondary-fixed-dim">{label}</span>
      {children}
      {hinweis && <span className="mt-1 block text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{hinweis}</span>}
    </label>
  );
}

function WahlKarte({ an, onWahl, titel, text, name, aus }: { an: boolean; onWahl: () => void; titel: string; text: ReactNode; name: string; aus?: boolean }) {
  return (
    <label className={`flex cursor-pointer gap-3 rounded-[12px] border p-3.5 transition-colors ${an ? "border-primary bg-primary/[0.04] dark:bg-primary/10" : "border-outline-variant/50 dark:border-outline/30"} ${aus ? "cursor-not-allowed opacity-50" : ""}`}>
      <Radio name={name} checked={an} onChange={onWahl} disabled={aus} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{titel}</span>
        <span className="mt-0.5 block text-[12px] text-secondary dark:text-secondary-fixed-dim">{text}</span>
      </span>
    </label>
  );
}

function VorschauKasten({ children }: { children: ReactNode }) {
  return <div className="mt-5 rounded-[12px] bg-[#FBF8F1] p-4 text-[12.5px] dark:bg-surface-variant/15">{children}</div>;
}
function VZeile({ l, r, stark }: { l: ReactNode; r: ReactNode; stark?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 py-0.5 ${stark ? "mt-1 border-t border-outline-variant/40 pt-2 font-semibold text-on-background dark:text-inverse-on-surface" : "text-on-surface-variant dark:text-secondary-fixed-dim"}`}>
      <span>{l}</span>
      <span className="tabular-nums">{r}</span>
    </div>
  );
}

const FUSS = (onClose: () => void, busy: boolean, ok: string, disabled?: boolean) => (
  <>
    <Button type="button" variant="secondary" onClick={onClose}>
      Abbrechen
    </Button>
    <Button type="submit" form="abr-drawer-form" disabled={busy || disabled}>
      <Icon name="check" className="text-[18px]" /> {busy ? "Speichert …" : ok}
    </Button>
  </>
);

/* ─────────────────────────────── Rabatt / Zuschlag ─────────────────────────────── */

export function RabattDrawer({ stand, zeile, zuschlag, onClose, onSpeichern, busy }: { stand: AbrAntwort; zeile: AbrZeile | null; zuschlag?: boolean; onClose: () => void; onSpeichern: (z: AbrZeileEingabe) => void; busy: boolean }) {
  const [modus, setModus] = useState<"prozent" | "euro" | "zuschlag">(zeile?.art === "zuschlag" || zuschlag ? "zuschlag" : zeile?.betragCents ? "euro" : "prozent");
  const [wert, setWert] = useState(zeile ? (zeile.prozentBp ? bpAlsEingabe(zeile.prozentBp) : eurFeld(zeile.betragCents)) : "5");
  const [zuschlagProzent, setZuschlagProzent] = useState(!(zeile?.art === "zuschlag" && zeile.betragCents));
  const [bezug, setBezug] = useState<Bezug>(zeile?.bezug ?? "leistungen_darueber");
  // Stufe 2: Bezug „bestimmte Zeilen“ — wählbar sind gespeicherte Leistungs- und Mehrverbrauchszeilen mit Betrag.
  const kandidaten = stand.zeilen.filter((z) => (z.art === "leistung" || z.art === "mehrverbrauch") && (z.einzelpreisCents ?? 0) > 0 && z.steuersatzBp != null);
  // #1491 M2: eine gelöschte Bezugszeile fällt hier sichtbar heraus (die Checkliste zeigt, was bleibt).
  const [gewaehlt, setGewaehlt] = useState<string[]>((zeile?.bezugZeilen ?? []).filter((id) => kandidaten.some((k) => k.id === id)));
  const umschalten = (id: string) => setGewaehlt((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));
  const gruppen = [
    { key: "speisen", label: "Alle zu 7 %", ids: kandidaten.filter((z) => z.steuersatzBp === 700).map((z) => z.id) },
    { key: "getraenke", label: "Alle zu 19 %", ids: kandidaten.filter((z) => z.steuersatzBp === 1900).map((z) => z.id) },
    { key: "mehr", label: "Mehrverbrauch", ids: kandidaten.filter((z) => z.art === "mehrverbrauch").map((z) => z.id) },
  ].filter((g) => g.ids.length > 0);
  const prozent = modus === "prozent" || (modus === "zuschlag" && zuschlagProzent);
  const zahl = prozent ? parseGermanDecimal(wert) : null;
  const cents = prozent ? null : parseGermanEurosToCents(wert);
  const neu: AbrZeileEingabe = {
    ...(zeile ? { id: zeile.id } : {}),
    art: modus === "zuschlag" ? "zuschlag" : "rabatt",
    text: modus === "zuschlag" ? "Zuschlag" : "Rabatt",
    prozentBp: prozent && zahl != null ? Math.round(zahl * 100) : null,
    betragCents: !prozent ? cents : null,
    bezug,
    bezugZeilen: bezug === "zeilen" ? gewaehlt : null,
  };
  const zeilen: AbrZeileEingabe[] = stand.zeilen.map((z) => (zeile && z.id === zeile.id ? neu : z));
  if (!zeile) zeilen.splice(Math.min(einfuegeIndexRabatt(stand.zeilen), zeilen.length), 0, neu);
  const v = vorschau(zeilen, stand.abrechnung?.gaeste ?? 0);
  const idx = zeile ? zeilen.findIndex((z) => z.id === zeile.id) : zeilen.indexOf(neu);
  const e = v.ergebnis?.zeilen[idx];
  const gueltig = (prozent ? zahl != null && zahl > 0 : cents != null && cents > 0) && v.ergebnis != null && (bezug !== "zeilen" || gewaehlt.length > 0);
  return (
    <Drawer open onClose={onClose} title={zeile ? (zeile.art === "zuschlag" ? "Zuschlag" : "Rabatt") : "Rabatt oder Zuschlag"} subtitle="Rabatt oder Zuschlag in % oder €, mit Bezug. Die Aufteilung auf die Steuersätze rechnet MAESTRO." breite="schmal" footer={FUSS(onClose, busy, "Übernehmen", !gueltig)}>
      <form id="abr-drawer-form" onSubmit={(ev) => { ev.preventDefault(); if (gueltig) onSpeichern(neu); }} className="space-y-5">
        <Segmente label="Art" wert={modus} onWahl={setModus} werte={[{ key: "prozent", label: "Rabatt %" }, { key: "euro", label: "Rabatt €" }, { key: "zuschlag", label: "Zuschlag" }]} />
        {modus === "zuschlag" && (
          <Segmente label="Zuschlag in" wert={zuschlagProzent ? "p" : "e"} onWahl={(k) => setZuschlagProzent(k === "p")} werte={[{ key: "p", label: "%" }, { key: "e", label: "€" }]} />
        )}
        <Feld label="Wert">
          <Input inputMode="decimal" value={wert} onChange={(ev) => setWert(ev.target.value)} aria-label="Wert" />
        </Feld>
        <div>
          <span className="mb-2 block font-label-md text-[12px] font-semibold text-on-surface-variant dark:text-secondary-fixed-dim">Bezug</span>
          <div className="space-y-2">
            <WahlKarte name="bezug" an={bezug === "leistungen_darueber"} onWahl={() => setBezug("leistungen_darueber")} titel="Alle Leistungen darüber" text="Standard. Wirkt nicht auf Mehrverbrauch darunter und nie auf andere Rabatte." />
            <WahlKarte name="bezug" an={bezug === "zeilen"} onWahl={() => setBezug("zeilen")} aus={kandidaten.length === 0} titel="Bestimmte Zeilen" text="Zum Beispiel nur „Anteil Speisen“ oder nur Getränke. Gilt unabhängig davon, wo die Zeile steht." />
            {bezug === "zeilen" && (
              <div className="rounded-[12px] border border-outline-variant/50 p-3 dark:border-outline/30" data-testid="abr-bezug-zeilen">
                {gruppen.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {gruppen.map((g) => {
                      const alle = g.ids.every((id) => gewaehlt.includes(id));
                      return (
                        <button
                          key={g.key}
                          type="button"
                          aria-pressed={alle}
                          onClick={() => setGewaehlt((x) => (alle ? x.filter((id) => !g.ids.includes(id)) : [...new Set([...x, ...g.ids])]))}
                          className={`rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${alle ? "border-primary bg-primary/10 text-primary" : "border-outline-variant/60 text-on-surface-variant hover:border-primary dark:border-outline/40 dark:text-secondary-fixed-dim"}`}
                        >
                          {g.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <ul className="space-y-1">
                  {kandidaten.map((z) => (
                    <li key={z.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-[8px] px-1.5 py-1 text-[12.5px] hover:bg-surface-container-high dark:hover:bg-surface-variant/20">
                        <Checkbox checked={gewaehlt.includes(z.id)} onChange={() => umschalten(z.id)} aria-label={zeilenTitel(z)} />
                        <span className="min-w-0 flex-1 truncate text-on-background dark:text-inverse-on-surface">{zeilenTitel(z)}</span>
                        <span className="shrink-0 text-[11px] text-secondary">{satzText(z.steuersatzBp)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
                {gewaehlt.length === 0 && <p className="mt-1 text-[11.5px] font-semibold text-[#6F675A]">Mindestens eine Zeile wählen.</p>}
              </div>
            )}
            <WahlKarte name="bezug" an={bezug === "zwischensumme"} onWahl={() => setBezug("zwischensumme")} titel="Zwischensumme bis hier" text="Wirkt auf alles darüber, auch auf andere Rabatte. Nur auf ausdrückliche Wahl." />
          </div>
        </div>
        {v.fehler && <ErrorNote message={v.fehler} />}
        {e && (
          <VorschauKasten>
            <VZeile l="Basis" r={money(e.basisCents ?? 0)} />
            {e.jeSatz.map((s) => (
              <VZeile key={s.rateBp} l={`davon zu ${satzText(s.rateBp)}`} r={money(s.cents)} />
            ))}
            <VZeile stark l={modus === "zuschlag" ? "Zuschlag gesamt" : "Rabatt gesamt"} r={money(e.bruttoCents)} />
          </VorschauKasten>
        )}
        <p className="text-[11.5px] text-secondary dark:text-secondary-fixed-dim">Mehrere Rabatte sind möglich. Jeder zeigt seine Basis, damit nie still „Rabatt auf Rabatt“ entsteht. Die Reihenfolge ändert der Griff an der Zeile.</p>
      </form>
    </Drawer>
  );
}

/* ─────────────────────────── Mehr- oder Minderverbrauch / Leistung ─────────────────────────── */

export function BetragsDrawer({ stand, zeile, art, wo: woStart, onClose, onSpeichern, busy }: { stand: AbrAntwort; zeile: AbrZeile | null; art: "leistung" | "mehrverbrauch"; wo?: "ueber" | "unter"; onClose: () => void; onSpeichern: (z: AbrZeileEingabe, index: number | null) => void; busy: boolean }) {
  const istLeistung = art === "leistung";
  const [minder, setMinder] = useState((zeile?.einzelpreisCents ?? 0) < 0);
  const [text, setText] = useState(zeile?.text ?? (istLeistung ? "" : "Mehrverbrauch Getränke"));
  const [beschreibung, setBeschreibung] = useState(zeile?.beschreibung ?? "");
  const [betrag, setBetrag] = useState(eurFeld(zeile?.einzelpreisCents != null ? Math.abs(zeile.einzelpreisCents) : null));
  const [menge, setMenge] = useState(zeile ? ((zeile.mengeMilli ?? 1000) / 1000).toLocaleString("de-DE") : "1");
  const [proPerson, setProPerson] = useState(zeile?.proPerson ?? false);
  const [satz, setSatz] = useState<string>(String(zeile?.steuersatzBp ?? (istLeistung ? 700 : 1900)));
  const [wo, setWo] = useState<"ueber" | "unter">(woStart ?? (istLeistung ? "ueber" : "unter"));
  const cents = parseGermanEurosToCents(betrag);
  const mengeZahl = parseGermanDecimal(menge);
  const neu: AbrZeileEingabe = {
    ...(zeile ? { id: zeile.id } : {}),
    art: zeile?.art === "gutschrift" ? "gutschrift" : art,
    text: text.trim(),
    beschreibung: beschreibung.trim() || null,
    mengeMilli: istLeistung && mengeZahl != null ? Math.round(mengeZahl * 1000) : 1000,
    einzelpreisCents: cents == null ? null : minder && !istLeistung ? -cents : cents,
    steuersatzBp: Number(satz),
    einheit: istLeistung ? (proPerson ? "Person(en)" : zeile?.einheit ?? "Stück") : "Pauschal",
    proPerson: istLeistung ? proPerson : false,
  };
  const zeilen: AbrZeileEingabe[] = stand.zeilen.map((z) => (zeile && z.id === zeile.id ? neu : z));
  const index = zeile ? null : einfuegeIndex(stand.zeilen, wo);
  if (!zeile) zeilen.splice(index!, 0, neu);
  const gaeste = stand.abrechnung?.gaeste ?? 0;
  const v = vorschau(zeilen, gaeste);
  const e = v.ergebnis?.zeilen[zeile ? zeilen.findIndex((z) => z.id === zeile.id) : index!];
  const rabatt = stand.zeilen.find((z) => z.art === "rabatt");
  const gueltig = text.trim().length > 0 && cents != null && cents > 0 && (!istLeistung || (mengeZahl != null && mengeZahl >= 0)) && v.ergebnis != null;
  return (
    <Drawer
      open
      onClose={onClose}
      title={istLeistung ? (zeile ? "Leistung bearbeiten" : "Leistung hinzufügen") : "Mehr- oder Minderverbrauch"}
      subtitle={istLeistung ? "Menge × Einzelpreis (brutto) zu einem Steuersatz." : "Was am Abend mehr oder weniger war. Ein Minderverbrauch ist dieselbe Zeile mit Minus."}
      breite="schmal"
      footer={FUSS(onClose, busy, zeile ? "Übernehmen" : "Hinzufügen", !gueltig)}
    >
      <form id="abr-drawer-form" onSubmit={(ev) => { ev.preventDefault(); if (gueltig) onSpeichern(neu, index); }} className="space-y-5">
        {!istLeistung && <Segmente label="Art" wert={minder ? "minder" : "mehr"} onWahl={(k) => setMinder(k === "minder")} werte={[{ key: "mehr", label: "Mehrverbrauch" }, { key: "minder", label: "Minderverbrauch" }]} />}
        <Feld label="Text auf der Rechnung">
          <Input value={text} onChange={(ev) => setText(ev.target.value)} maxLength={500} />
        </Feld>
        {istLeistung && (
          <Feld label="Beschreibung (optional)">
            <Textarea rows={2} value={beschreibung} onChange={(ev) => setBeschreibung(ev.target.value)} maxLength={2000} />
          </Feld>
        )}
        <div className="grid grid-cols-2 gap-3">
          {istLeistung && (
            <Feld label={proPerson ? "Menge je Gast" : "Menge"} hinweis={proPerson ? `× ${gaeste} Gäste` : undefined}>
              <Input inputMode="decimal" value={menge} onChange={(ev) => setMenge(ev.target.value)} />
            </Feld>
          )}
          <Feld label={istLeistung ? "Einzelpreis (brutto)" : "Betrag (brutto)"}>
            <Input inputMode="decimal" value={betrag} onChange={(ev) => setBetrag(ev.target.value)} placeholder="0,00 €" />
          </Feld>
        </div>
        {istLeistung && (
          <label className="flex items-center gap-2 text-[13px] text-on-background dark:text-inverse-on-surface">
            <Checkbox checked={proPerson} onChange={(ev) => setProPerson(ev.target.checked)} /> Pro Person (folgt dem Gästefeld oben)
          </label>
        )}
        <div>
          <span className="mb-2 block font-label-md text-[12px] font-semibold text-on-surface-variant dark:text-secondary-fixed-dim">Steuersatz</span>
          <Segmente label="Steuersatz" wert={satz} onWahl={setSatz} werte={[{ key: "700", label: "7 % Speisen" }, { key: "1900", label: "19 % Getränke" }, { key: "0", label: "0 %" }]} />
        </div>
        {!zeile && rabatt && (
          <div>
            <span className="mb-2 block font-label-md text-[12px] font-semibold text-on-surface-variant dark:text-secondary-fixed-dim">Position</span>
            <div className="space-y-2">
              <WahlKarte name="wo" an={wo === "unter"} onWahl={() => setWo("unter")} titel="Unter dem Rabatt" text={istLeistung ? "Ohne Rabatt, wie Mehrverbrauch." : "Ohne Rabatt. Standard für Mehrverbrauch."} />
              <WahlKarte name="wo" an={wo === "ueber"} onWahl={() => setWo("ueber")} titel="Über dem Rabatt" text="Wird mit dem Rabatt darunter rabattiert." />
            </div>
          </div>
        )}
        {v.fehler && <ErrorNote message={v.fehler} />}
        {v.ergebnis && stand.ergebnis && e && (
          <VorschauKasten>
            <VZeile l="Diese Zeile" r={money(e.bruttoCents)} />
            {e.jeSatz.map((s) => (
              <VZeile key={s.rateBp} l={`USt ${satzText(s.rateBp)} auf diese Zeile`} r={money(vatFromGross(s.cents, s.rateBp))} />
            ))}
            <VZeile stark l="Neu: Rechnungsbetrag" r={`${money(v.ergebnis.summeCents)} statt ${money(stand.ergebnis.summeCents)}`} />
          </VorschauKasten>
        )}
      </form>
    </Drawer>
  );
}

/* ─────────────────────────── Außerhalb von MAESTRO (Pflichtfrage) ─────────────────────────── */

interface ExtZahlung { betrag: string; datum: string; zahlweise: string; beleg: string }
interface ExtRechnung { nummer: string; datum: string; b7: string; b19: string }

export function AusserhalbDrawer({ stand, onClose, onSpeichern, busy }: { stand: AbrAntwort; onClose: () => void; onSpeichern: (b: unknown) => void; busy: boolean }) {
  const [ja, setJa] = useState<boolean | null>(stand.abrechnung?.ausserhalbBeantwortet ?? null);
  const [zahlungen, setZahlungen] = useState<ExtZahlung[]>(
    stand.extern.zahlungen.length ? stand.extern.zahlungen.map((z) => ({ betrag: eurFeld(z.betragCents), datum: z.datum ?? "", zahlweise: z.zahlweise ?? "ueberweisung", beleg: z.beleg ?? "" })) : [{ betrag: "", datum: "", zahlweise: "ueberweisung", beleg: "" }],
  );
  const [rechnungen, setRechnungen] = useState<ExtRechnung[]>(
    stand.extern.rechnungen.map((r) => ({ nummer: r.nummer ?? "", datum: r.datum ?? "", b7: eurFeld(r.vatBreakdown?.find((b) => b.rateBp === 700)?.grossCents ?? 0), b19: eurFeld(r.vatBreakdown?.find((b) => b.rateBp === 1900)?.grossCents ?? 0) })),
  );
  const zGefuellt = zahlungen.filter((z) => z.betrag.trim() !== "");
  const rGefuellt = rechnungen.filter((r) => r.nummer.trim() !== "");
  const gueltig = ja === false || (ja === true && (zGefuellt.length > 0 || rGefuellt.length > 0) && zGefuellt.every((z) => (parseGermanEurosToCents(z.betrag) ?? 0) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(z.datum)) && rGefuellt.every((r) => /^\d{4}-\d{2}-\d{2}$/.test(r.datum)));
  const setZ = (i: number, t: Partial<ExtZahlung>) => setZahlungen((l) => l.map((z, j) => (j === i ? { ...z, ...t } : z)));
  const setR = (i: number, t: Partial<ExtRechnung>) => setRechnungen((l) => l.map((r, j) => (j === i ? { ...r, ...t } : r)));
  const absenden = () =>
    onSpeichern({
      antwort: ja ? "ja" : "nein",
      zahlungen: ja ? zGefuellt.map((z) => ({ betragCents: parseGermanEurosToCents(z.betrag), datum: z.datum, zahlweise: z.zahlweise, beleg: z.beleg.trim() || null })) : [],
      rechnungen: ja ? rGefuellt.map((r) => ({ nummer: r.nummer.trim(), datum: r.datum, brutto7Cents: parseGermanEurosToCents(r.b7) ?? 0, brutto19Cents: parseGermanEurosToCents(r.b19) ?? 0 })) : [],
    });
  return (
    <Drawer open onClose={onClose} title="Zahlungen oder Rechnungen außerhalb von MAESTRO?" subtitle="Pflichtfrage vor jedem Abschluss. Alles, was im Altsystem, direkt in LexOffice oder bar gelaufen ist, muss hier stehen, sonst zahlt der Gast doppelt." footer={FUSS(onClose, busy, "Speichern", !gueltig)}>
      <form id="abr-drawer-form" onSubmit={(ev) => { ev.preventDefault(); if (gueltig) absenden(); }} className="space-y-5">
        <div className="grid gap-2 sm:grid-cols-2">
          <WahlKarte name="aus" an={ja === false} onWahl={() => setJa(false)} titel="Nein" text="Alles ist in MAESTRO erfasst." />
          <WahlKarte name="aus" an={ja === true} onWahl={() => setJa(true)} titel="Ja, hier erfassen" text="Zahlungen und/oder Rechnungen" />
        </div>
        {ja && (
          <>
            <section>
              <h3 className="mb-2 text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">Zahlungen außerhalb</h3>
              <div className="space-y-3">
                {zahlungen.map((z, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2 rounded-[12px] border border-outline-variant/40 p-3 sm:grid-cols-4 dark:border-outline/30">
                    <Feld label="Betrag"><Input inputMode="decimal" value={z.betrag} onChange={(ev) => setZ(i, { betrag: ev.target.value })} placeholder="0,00 €" /></Feld>
                    <Feld label="Datum"><DateInput value={z.datum} onValueChange={(v) => setZ(i, { datum: v })} aria-label="Datum" /></Feld>
                    <Feld label="Zahlweise">
                      <Select value={z.zahlweise} onValueChange={(v) => setZ(i, { zahlweise: v })} ariaLabel="Zahlweise" options={[{ value: "ueberweisung", label: "Überweisung" }, { value: "ec_karte", label: "EC-Karte" }, { value: "bar", label: "Bar" }, { value: "stripe", label: "Stripe" }]} />
                    </Feld>
                    <Feld label="Beleg"><Input value={z.beleg} onChange={(ev) => setZ(i, { beleg: ev.target.value })} placeholder="z. B. Kontoauszug" /></Feld>
                  </div>
                ))}
              </div>
              <Button type="button" variant="ghost" className="mt-2" onClick={() => setZahlungen((l) => [...l, { betrag: "", datum: "", zahlweise: "ueberweisung", beleg: "" }])}>
                <Icon name="add" className="text-[18px]" /> Weitere Zahlung
              </Button>
              <p className="mt-1 text-[11.5px] text-secondary dark:text-secondary-fixed-dim">Steht auf der Rechnung als „bereits bezahlt“. Der Rechnungsbetrag bleibt dabei unverändert.</p>
            </section>
            <section>
              <h3 className="mb-2 text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">Anzahlungs- oder Teilrechnungen außerhalb</h3>
              <div className="space-y-3">
                {rechnungen.map((r, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2 rounded-[12px] border border-outline-variant/40 p-3 sm:grid-cols-4 dark:border-outline/30">
                    <Feld label="Rechnungsnummer"><Input value={r.nummer} onChange={(ev) => setR(i, { nummer: ev.target.value })} /></Feld>
                    <Feld label="Datum"><DateInput value={r.datum} onValueChange={(v) => setR(i, { datum: v })} aria-label="Datum" /></Feld>
                    <Feld label="Brutto zu 7 %"><Input inputMode="decimal" value={r.b7} onChange={(ev) => setR(i, { b7: ev.target.value })} placeholder="0,00 €" /></Feld>
                    <Feld label="Brutto zu 19 %"><Input inputMode="decimal" value={r.b19} onChange={(ev) => setR(i, { b19: ev.target.value })} placeholder="0,00 €" /></Feld>
                  </div>
                ))}
                {rechnungen.length === 0 && <p className="text-[12.5px] text-secondary dark:text-secondary-fixed-dim">Keine Rechnung außerhalb von MAESTRO.</p>}
              </div>
              <Button type="button" variant="ghost" className="mt-2" onClick={() => setRechnungen((l) => [...l, { nummer: "", datum: "", b7: "", b19: "" }])}>
                <Icon name="add" className="text-[18px]" /> Rechnung erfassen
              </Button>
              <p className="mt-1 text-[11.5px] text-secondary dark:text-secondary-fixed-dim">Eine erfasste Rechnung zieht die Schlussrechnung je Steuersatz als „bereits abgerechnet“ ab (§ 14 Abs. 5 UStG), genau wie eine Anzahlungsrechnung aus MAESTRO.</p>
            </section>
          </>
        )}
      </form>
    </Drawer>
  );
}

/* ─────────────────────────── Tor 1: Vorschau der Rechnung ─────────────────────────── */

export function VorschauDrawer({ daten, fehler, laden, kunde, onClose, onAnlegen, busy, darf, storno }: { daten: VorschauDaten | null; fehler: string | null; laden: boolean; kunde: string; onClose: () => void; onAnlegen: () => void; busy: boolean; darf: boolean; storno?: { invoiceNumber: string | null; amountGrossCents: number | null } | null }) {
  const [bestaetigt, setBestaetigt] = useState(false);
  useEffect(() => setBestaetigt(false), [daten]);
  const p = daten?.payload;
  // Negativer Rest: LexOffice bekommt eine Rechnungskorrektur mit umgekehrtem Vorzeichen. Angezeigt wird
  // der Beleg aus Sicht der Abrechnung (Leistungen positiv, Abzüge negativ).
  const f = daten?.lexArt === "gutschrift" ? -1 : 1;
  const d = (iso: string | undefined) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "–");
  const gesperrt = !daten || !daten.pruefung.inOrdnung || daten.offenePunkte.length > 0 || !darf;
  return (
    <Drawer
      open
      onClose={onClose}
      title={daten?.invoiceType === "final" ? "Schlussrechnung prüfen" : "Rechnung prüfen"}
      subtitle="Genau diese Zeilen gehen an LexOffice, aus derselben Payload gezeichnet."
      breite="breit"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button type="button" disabled={gesperrt || busy || !bestaetigt} onClick={onAnlegen}>
            <Icon name="verified" className="text-[18px]" /> {busy ? "Rechnung wird in LexOffice angelegt …" : "In LexOffice anlegen"}
          </Button>
        </>
      }
    >
      {laden && <p className="text-[13px] text-secondary">Lädt …</p>}
      {fehler && <ErrorNote message={fehler} />}
      {p && daten && (
        <div className="space-y-5">
          <div className="rounded-[12px] border border-[#EFE8DA] bg-white p-5 text-[12.5px] dark:border-outline/30 dark:bg-night-card">
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <p className="text-[11px] text-secondary">Absender: Anschrift des Betriebs laut LexOffice</p>
                <p className="mt-3 text-[11px] text-secondary">An</p>
                <p className="font-semibold text-on-background dark:text-inverse-on-surface">{p.address.name || kunde}</p>
                <p className="text-on-surface-variant dark:text-secondary-fixed-dim">{daten.anschriftVollstaendig ? [p.address.street, [p.address.zip, p.address.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : "Straße Nr., PLZ Ort (nach Ergänzung)"}</p>
              </div>
              <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-right">
                <dt className="text-secondary">Rechnungsnr.</dt><dd>vergibt LexOffice</dd>
                <dt className="text-secondary">Rechnungsdatum</dt><dd>{d(p.voucherDate)}</dd>
                <dt className="text-secondary">Leistungsdatum</dt><dd>{p.shippingConditions ? `${d(p.shippingConditions.shippingDate)}${p.shippingConditions.shippingEndDate ? ` – ${d(p.shippingConditions.shippingEndDate)}` : ""}` : "siehe Hinweistext"}</dd>
              </dl>
            </div>
            <p className="mt-4 text-[15px] font-bold text-on-background dark:text-inverse-on-surface">{daten.invoiceType === "final" ? "Schlussrechnung" : "Rechnung"}</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[520px] text-left">
                <thead>
                  <tr className="border-b border-outline-variant/40 text-[10.5px] uppercase tracking-[0.07em] text-secondary">
                    <th className="py-1.5 pr-2">Pos.</th><th className="py-1.5 pr-2">Bezeichnung</th><th className="py-1.5 pr-2 text-right">Menge</th><th className="py-1.5 pr-2 text-right">Einzel brutto</th><th className="py-1.5 pr-2 text-right">USt</th><th className="py-1.5 text-right">Gesamt brutto</th>
                  </tr>
                </thead>
                <tbody>
                  {p.lineItems.map((l, i) => (
                    <tr key={i} className="border-b border-[#F6F0E4] align-top dark:border-outline/20">
                      <td className="py-1.5 pr-2 text-secondary">{i + 1}</td>
                      <td className="py-1.5 pr-2">{l.name}{l.description ? <span className="block text-[11px] text-secondary">{l.description}</span> : null}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{l.quantity.toLocaleString("de-DE")} {l.unitName === "Person(en)" ? "Pers." : ""}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{money(f * Math.round(l.unitPrice.grossAmount * 100))}</td>
                      <td className="py-1.5 pr-2 text-right">{l.unitPrice.taxRatePercentage} %</td>
                      <td className="py-1.5 text-right tabular-nums">{money(f * Math.round(l.quantity * l.unitPrice.grossAmount * 100))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ml-auto mt-3 max-w-[300px] space-y-0.5">
              <VZeile l="Summe netto" r={money(f * Math.round(p.totalPrice.totalNetAmount * 100))} />
              {daten.vatBreakdown.filter((b) => b.rateBp > 0).map((b) => (
                <VZeile key={b.rateBp} l={`USt ${satzText(b.rateBp)} auf ${money(b.netCents)}`} r={money(b.vatCents)} />
              ))}
              <VZeile stark l={f < 0 ? "Betrag zu Gunsten des Gastes" : "Rechnungsbetrag"} r={money(f * Math.round(p.totalPrice.totalGrossAmount * 100))} />
            </div>
            {f < 0 && <p className="mt-3 text-[12px] font-semibold text-on-surface-variant">Die Anzahlungen übersteigen die Leistungen. MAESTRO legt diese Schlussrechnung in LexOffice als Rechnungskorrektur an; danach entscheidest du Rückerstattung oder Guthaben.</p>}
            {p.remark && <p className="mt-4 whitespace-pre-line text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">{p.remark}</p>}
          </div>
          {!daten.pruefung.inOrdnung && <ErrorNote message={daten.pruefung.meldung ?? "Die Rechnung weicht von der Abrechnung ab."} />}
          {daten.offenePunkte.length > 0 && (
            <div className="rounded-[12px] border border-outline-variant bg-[#F0EAE0] p-4 text-[12.5px] text-[#6F675A] dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              <p className="flex items-center gap-2 font-semibold"><Icon name="error" className="text-[17px]" /> Vor dem Anlegen noch offen</p>
              <ul className="mt-1 list-disc pl-6">{daten.offenePunkte.map((x) => <li key={x}>{x}</li>)}</ul>
            </div>
          )}
          <div className="rounded-[12px] border border-outline-variant bg-[#F0EAE0] p-4 text-[#6F675A] dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]">
            <p className="flex items-center gap-2 font-semibold"><Icon name="verified" className="text-[18px]" /> Nummerierter Beleg, danach nur per Gutschrift korrigierbar</p>
            <p className="mt-1 text-[12.5px]">„In LexOffice anlegen“ erzeugt sofort eine echte, fortlaufend nummerierte Rechnung und sperrt die Abrechnung. An den Gast geht noch nichts, das ist der nächste Schritt.</p>
            {storno && <p className="mt-1 text-[12.5px] font-semibold">Vorher wird die bisherige Rechnung {storno.invoiceNumber} ({money(storno.amountGrossCents ?? 0)}) per Gutschrift storniert. Scheitert der Storno, entsteht keine neue Rechnung.</p>}
            <label className="mt-3 flex items-center gap-2 text-[12.5px] font-semibold">
              <Checkbox checked={bestaetigt} onChange={(ev) => setBestaetigt(ev.target.checked)} disabled={gesperrt} /> Zeilen und Beträge geprüft
            </label>
          </div>
        </div>
      )}
    </Drawer>
  );
}
